import {
  Button,
  Card,
  FieldError,
  Form as HeroForm,
  Input,
  Label,
  Modal,
  Separator,
  Table,
  TextArea,
  TextField,
  useOverlayState,
} from '@heroui/react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { Edit2, Plus, Trash2 } from 'lucide-react'
import * as React from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { useHandleError } from '~/hooks/use-handle-error'
import { authApiClient } from '~/lib/api-client'
import { DeleteCategoryModal } from './-components/delete-category-modal'
import { useDebounce } from 'use-debounce'

// 定义表单 Schema
const categorySchema = z.object({
  name: z.string().min(1, '名称为必填项'),
  sourceId: z.string().min(1, 'Source ID 为必填项'),
  description: z.string().optional(),
  cn: z.string().optional(),
  en: z.string().optional(),
  tw: z.string().optional(),
  kr: z.string().optional(),
})

type CategoryFormValues = z.infer<typeof categorySchema>

const categoriesSearchSchema = z.object({
  page: z.number().catch(1),
  pageSize: z.number().catch(20),
  cn: z.string().optional().catch(''),
  sourceId: z.string().optional().catch(''),
})

export const Route = createFileRoute('/admin/categories')({
  validateSearch: categoriesSearchSchema,
  component: CategoriesPage,
})

function CategoriesPage() {
  const { page, pageSize, cn, sourceId } = Route.useSearch()
  const navigate = Route.useNavigate()
  const queryClient = useQueryClient()
  const handleError = useHandleError()
  const modalState = useOverlayState()
  const [editingId, setEditingId] = React.useState<number | null>(null)
  const [deletingCategory, setDeletingCategory] = React.useState<{ id: number; name: string } | null>(null)

  const [localSearch, setLocalSearch] = React.useState(cn || '')
  const [localSourceId, setLocalSourceId] = React.useState(sourceId || '')
  const [debouncedSearch] = useDebounce(localSearch, 300)
  const [debouncedSourceId] = useDebounce(localSourceId, 300)

  React.useEffect(() => {
    if (debouncedSearch !== cn || debouncedSourceId !== sourceId) {
      navigate({ search: (prev) => ({ ...prev, cn: debouncedSearch, sourceId: debouncedSourceId, page: 1 }) })
    }
  }, [debouncedSearch, debouncedSourceId, cn, sourceId, navigate])

  // 服务端分页查询 — 获取全量分类数据
  const { data: categoriesData, isFetching } = useQuery({
    queryKey: ['admin', 'categories', page, pageSize, cn, sourceId],
    queryFn: async () => {
      const res = await authApiClient.categories.$get({
        query: { 
          page: String(page), 
          pageSize: String(pageSize), 
          sort: 'weight:desc',
          ...(cn ? { cn } : {}),
          ...(sourceId ? { sourceId } : {})
        } as any,
      })
      if (!res.ok) throw res
      return await res.json()
    },
    placeholderData: keepPreviousData,
  })

  const categories = categoriesData?.data || []

  // 表单初始化
  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm<CategoryFormValues>({
    resolver: zodResolver(categorySchema),
  })

  // 创建/更新 Mutation
  const mutation = useMutation({
    mutationFn: async (values: CategoryFormValues) => {
      const res = editingId
        ? await authApiClient.categories[':id'].$patch({ param: { id: String(editingId) }, json: values as any, header: { Authorization: '' } })
        : await authApiClient.categories.$post({ json: values as any, header: { Authorization: '' } })
      if (!res.ok)
        throw res
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'categories'] })
      toast.success(editingId ? '分类更新成功' : '分类创建成功')
      modalState.close()
      reset()
      setEditingId(null)
    },
    onError: handleError,
  })

  const onSubmit = (values: CategoryFormValues) => mutation.mutate(values)

  const handleEdit = React.useCallback((category: any) => {
    setEditingId(category.id)
    reset({
      name: category.name,
      sourceId: category.sourceId,
      description: category.description || '',
      cn: category.cn || '',
      en: category.en || '',
      tw: category.tw || '',
      kr: category.kr || '',
    })
    modalState.open()
  }, [reset, modalState])

  // TanStack Table Setup — 服务端分页
  const columns = React.useMemo(() => {
    const columnHelper = createColumnHelper<any>()
    return [
      columnHelper.accessor('name', {
        header: '名称',
        cell: info => (
          <div>
            <div className="font-bold">{info.getValue()}</div>
            <div className="text-xs text-muted">{info.row.original.description}</div>
          </div>
        ),
      }),
      columnHelper.accessor('sourceId', {
        header: '数据源 ID',
        cell: info => <code className="text-xs bg-muted px-1 rounded">{info.getValue()}</code>,
      }),
      columnHelper.accessor('weight', {
        header: '权重',
        cell: info => {
          const w = info.getValue()
          return <span className={`font-mono text-sm ${w > 0 ? 'font-bold text-orange-500' : 'text-muted'}`}>{w ?? 0}</span>
        },
      }),
      columnHelper.display({
        id: 'translations',
        header: '已配翻译',
        cell: info => {
          const item = info.row.original
          return (
            <div className="flex gap-1 flex-wrap">
              {['en', 'cn', 'tw', 'kr'].map(lang => (
                item[lang] && <span key={lang} className="text-[10px] uppercase border border-divider px-1 rounded">{lang}</span>
              ))}
            </div>
          )
        },
      }),
      columnHelper.accessor('createdAt', {
        header: '创建时间',
        cell: info => (
          <span className="text-xs text-muted">{new Date(info.getValue()).toLocaleDateString()}</span>
        ),
      }),
      columnHelper.display({
        id: 'actions',
        header: () => <div className="text-right">操作</div>,
        cell: info => {
          const item = info.row.original
          return (
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" isIconOnly onPress={() => handleEdit(item)}>
                <Edit2 className="size-4" />
              </Button>
              <Button variant="danger-soft" size="sm" isIconOnly aria-label={`删除 ${item.name}`} onPress={() => setDeletingCategory({ id: item.id, name: item.name })}>
                <Trash2 className="size-4" />
              </Button>
            </div>
          )
        },
      }),
    ]
  }, [handleEdit])

  const table = useReactTable({
    data: categories,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
  })

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">分类管理</h1>
          <p className="text-muted mt-1">管理店铺业务分类以及多语言翻译。</p>
        </div>
        <div className="flex items-center gap-3">
          <Input
            placeholder="搜索分类中文名..."
            className="w-full md:w-56"
            value={localSearch}
            onChange={e => setLocalSearch(e.target.value)}
          />
          <Input
            placeholder="源 ID (例如 cat_1)"
            className="w-full md:w-40"
            value={localSourceId}
            onChange={e => setLocalSourceId(e.target.value)}
          />
          <Button onPress={() => { setEditingId(null); reset(); modalState.open() }}>
            <Plus className="size-4 mr-2" />
            {' '}
            添加分类
          </Button>
        </div>
      </div>

      <Card variant="secondary" className={isFetching ? 'opacity-70 transition-opacity' : 'transition-opacity'}>
        <Table>
          <Table.ScrollContainer>
            <Table.Content aria-label="Categories table">
              <Table.Header>
                {table.getHeaderGroups().map(headerGroup => (
                  <React.Fragment key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <Table.Column key={header.id} isRowHeader={header.column.id === 'name'}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </Table.Column>
                    ))}
                  </React.Fragment>
                ))}
              </Table.Header>
              <Table.Body>
                {table.getRowModel().rows.map(row => (
                  <Table.Row key={row.id}>
                    {row.getVisibleCells().map(cell => (
                      <Table.Cell key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </Table.Cell>
                    ))}
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Content>
          </Table.ScrollContainer>
        </Table>
      </Card>

      {/* 服务端分页控件 */}
      {categoriesData?.pagination && categoriesData.pagination.totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <span className="text-sm text-muted">
            共 {categoriesData.pagination.total} 条记录，第 {categoriesData.pagination.currentPage} / {categoriesData.pagination.totalPages} 页
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              isDisabled={page <= 1}
              onPress={() => navigate({ search: (prev) => ({ ...prev, page: page - 1 }) })}
            >
              上一页
            </Button>
            {Array.from({ length: Math.min(5, categoriesData.pagination.totalPages) }, (_, i) => {
              const totalPages = categoriesData.pagination.totalPages
              let pageNum: number
              if (totalPages <= 5) {
                pageNum = i + 1
              } else if (page <= 3) {
                pageNum = i + 1
              } else if (page >= totalPages - 2) {
                pageNum = totalPages - 4 + i
              } else {
                pageNum = page - 2 + i
              }
              return (
                <Button
                  key={pageNum}
                  variant={pageNum === page ? 'primary' : 'ghost'}
                  size="sm"
                  onPress={() => navigate({ search: (prev) => ({ ...prev, page: pageNum }) })}
                >
                  {pageNum}
                </Button>
              )
            })}
            <Button
              variant="ghost"
              size="sm"
              isDisabled={page >= categoriesData.pagination.totalPages}
              onPress={() => navigate({ search: (prev) => ({ ...prev, page: page + 1 }) })}
            >
              下一页
            </Button>
          </div>
        </div>
      )}

      <Modal>
        <Modal.Backdrop isOpen={modalState.isOpen} onOpenChange={open => !open && modalState.close()} variant="blur">
          <Modal.Container size="lg">
            <Modal.Dialog>
              <Modal.CloseTrigger />
              <Modal.Header>
                <Modal.Heading>{editingId ? '编辑分类' : '添加分类'}</Modal.Heading>
                <p className="text-sm text-muted">
                  {editingId ? '修改此业务分类的属性和翻译。' : '创建一个新的业务分类并配置其多语言翻译。'}
                </p>
              </Modal.Header>
              <Modal.Body>
                <HeroForm className="flex flex-col gap-4" onSubmit={handleSubmit(onSubmit)}>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex flex-col gap-1">
                      <Label>内部名称 (Name)</Label>
                      <Input {...register('name')} placeholder="e.g. Japanese" />
                      {errors.name && <FieldError>{errors.name.message}</FieldError>}
                    </div>
                    <div className="flex flex-col gap-1">
                      <Label>数据源 ID (Source ID)</Label>
                      <Input {...register('sourceId')} placeholder="e.g. cat_1" />
                      {errors.sourceId && <FieldError>{errors.sourceId.message}</FieldError>}
                    </div>
                  </div>

                  <div className="flex flex-col gap-1">
                    <Label>描述 (Description)</Label>
                    <TextArea {...register('description')} placeholder="分类描述（可选）" />
                    {errors.description && <FieldError>{errors.description.message}</FieldError>}
                  </div>

                  <Separator className="my-2" />
                  
                  <div>
                    <Label className="text-sm font-semibold">多语言翻译</Label>
                    <p className="text-xs text-muted mb-4">填写前端向用户展示的翻译名称。</p>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex flex-col gap-1">
                        <Label>简体中文</Label>
                        <Input {...register('cn')} placeholder="日料" />
                        {errors.cn && <FieldError>{errors.cn.message}</FieldError>}
                      </div>
                      <div className="flex flex-col gap-1">
                        <Label>英文 (English)</Label>
                        <Input {...register('en')} placeholder="Japanese Cuisine" />
                        {errors.en && <FieldError>{errors.en.message}</FieldError>}
                      </div>
                      <div className="flex flex-col gap-1">
                        <Label>繁体中文</Label>
                        <Input {...register('tw')} placeholder="日式料理" />
                        {errors.tw && <FieldError>{errors.tw.message}</FieldError>}
                      </div>
                      <div className="flex flex-col gap-1">
                        <Label>韩文 (Korean)</Label>
                        <Input {...register('kr')} placeholder="일식" />
                        {errors.kr && <FieldError>{errors.kr.message}</FieldError>}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex justify-end gap-3 mt-6">
                    <Button variant="ghost" onPress={() => modalState.close()}>取消</Button>
                    <Button type="submit" isPending={isSubmitting}>
                      {editingId ? '保存更改' : '创建分类'}
                    </Button>
                  </div>
                </HeroForm>
              </Modal.Body>
            </Modal.Dialog>
          </Modal.Container>
        </Modal.Backdrop>
      </Modal>

      <DeleteCategoryModal
        isOpen={!!deletingCategory}
        onClose={() => setDeletingCategory(null)}
        category={deletingCategory}
      />
    </div>
  )
}
